/**
 * FridgeIQ — DynamoDB Service
 * Queries fridgeiq-sessions, fridgeiq-inventory, fridgeiq-restock-log tables.
 */

import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import {
  DynamoDBDocumentClient,
  QueryCommand,
  PutCommand,
  UpdateCommand,
} from "@aws-sdk/lib-dynamodb";

const client = new DynamoDBClient({
  region: process.env.REACT_APP_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  },
});

const db = DynamoDBDocumentClient.from(client);

// ── Sessions ──────────────────────────────────────────────────────────────────

export async function getSessions(machineId, options = {}) {
  const { status, limit = 100, startKey } = options;

  const params = {
    TableName: "fridgeiq-sessions",
    KeyConditionExpression: "machine_id = :mid",
    ExpressionAttributeValues: { ":mid": machineId },
    ScanIndexForward: false,
    Limit: limit,
  };

  if (status) {
    params.FilterExpression = "#s = :status";
    params.ExpressionAttributeNames = { "#s": "status" };
    params.ExpressionAttributeValues[":status"] = status;
  }

  if (startKey) params.ExclusiveStartKey = startKey;

  try {
    const result = await db.send(new QueryCommand(params));
    return { items: result.Items || [], lastKey: result.LastEvaluatedKey };
  } catch (err) {
    console.error("getSessions error:", err);
    return { items: [], lastKey: null };
  }
}

export async function updateSessionNotes(machineId, sessionId, notes) {
  try {
    await db.send(
      new UpdateCommand({
        TableName: "fridgeiq-sessions",
        Key: { machine_id: machineId, session_id: sessionId },
        UpdateExpression: "SET operator_notes = :notes",
        ExpressionAttributeValues: { ":notes": notes },
      }),
    );
    return true;
  } catch (err) {
    console.error("updateSessionNotes error:", err);
    return false;
  }
}

export async function resolveAbnormalSession(machineId, sessionId) {
  try {
    await db.send(
      new UpdateCommand({
        TableName: "fridgeiq-sessions",
        Key: { machine_id: machineId, session_id: sessionId },
        UpdateExpression: "SET resolved = :r, #s = :s",
        ExpressionAttributeNames: { "#s": "status" },
        ExpressionAttributeValues: { ":r": true, ":s": "resolved" },
      }),
    );
    return true;
  } catch (err) {
    console.error("resolveAbnormalSession error:", err);
    return false;
  }
}

export function calcDayStats(sessions) {
  const today = new Date().toISOString().split("T")[0];
  const todaySessions = sessions.filter((s) => s.date === today);
  const revenue = todaySessions
    .filter((s) => s.status === "paid")
    .reduce((sum, s) => sum + (Number(s.grand_total) || 0), 0);
  const transactions = todaySessions.filter((s) => s.status !== "empty").length;
  const abnormal = todaySessions.filter((s) => s.status === "abnormal").length;
  const avgTicket = transactions > 0 ? revenue / transactions : 0;
  return {
    revenue: parseFloat(revenue.toFixed(2)),
    transactions,
    abnormal,
    avgTicket: parseFloat(avgTicket.toFixed(2)),
  };
}

// ── Inventory ─────────────────────────────────────────────────────────────────

export async function getInventory(machineId) {
  try {
    const result = await db.send(
      new QueryCommand({
        TableName: "fridgeiq-inventory",
        KeyConditionExpression: "machine_id = :mid",
        ExpressionAttributeValues: { ":mid": machineId },
      }),
    );
    return result.Items || [];
  } catch (err) {
    console.error("getInventory error:", err);
    return [];
  }
}

/**
 * Add units to a SKU and write a restock log entry.
 */
export async function restockItem(machineId, sku, qtyAdded, restockedBy) {
  const now = new Date().toISOString();
  try {
    // 1. Increment inventory quantity
    const result = await db.send(
      new UpdateCommand({
        TableName: "fridgeiq-inventory",
        Key: { machine_id: machineId, sku },
        UpdateExpression:
          "SET quantity = if_not_exists(quantity, :zero) + :qty, " +
          "last_restocked = :ts, restocked_by = :who",
        ExpressionAttributeValues: {
          ":qty": qtyAdded,
          ":zero": 0,
          ":ts": now,
          ":who": restockedBy,
        },
        ReturnValues: "ALL_NEW",
      }),
    );

    // 2. Write restock log entry
    await db.send(
      new PutCommand({
        TableName: "fridgeiq-restock-log",
        Item: {
          machine_id: machineId,
          timestamp: now,
          sku,
          name: result.Attributes?.name || sku,
          qty_added: qtyAdded,
          restocked_by: restockedBy,
          new_quantity: result.Attributes?.quantity || qtyAdded,
        },
      }),
    );

    return true;
  } catch (err) {
    console.error("restockItem error:", err);
    return false;
  }
}

/**
 * Get restock history for a machine, newest first.
 */
export async function getRestockLog(machineId) {
  try {
    const result = await db.send(
      new QueryCommand({
        TableName: "fridgeiq-restock-log",
        KeyConditionExpression: "machine_id = :mid",
        ExpressionAttributeValues: { ":mid": machineId },
        ScanIndexForward: false,
        Limit: 50,
      }),
    );
    return result.Items || [];
  } catch (err) {
    console.error("getRestockLog error:", err);
    return [];
  }
}

// ── Product Library ───────────────────────────────────────────────────────────

/**
 * Get all products from fridgeiq-products table.
 */
export async function getProducts() {
  try {
    const { ScanCommand } = await import("@aws-sdk/lib-dynamodb");
    const result = await db.send(
      new ScanCommand({
        TableName: "fridgeiq-products",
      }),
    );
    return (result.Items || []).sort((a, b) => a.name?.localeCompare(b.name));
  } catch (err) {
    console.error("getProducts error:", err);
    return [];
  }
}

/**
 * Save a product to fridgeiq-products table.
 * Creates or updates.
 */
export async function saveProduct(product) {
  try {
    await db.send(
      new PutCommand({
        TableName: "fridgeiq-products",
        Item: {
          sku: product.sku,
          gtin: product.gtin || "",
          name: product.name,
          brand: product.brand || "",
          category: product.category,
          price: product.price,
          cost: product.cost || 0,
          tax_rate: product.tax_rate || 0,
          image_url: product.image_url || "",
          image_s3: product.image_s3 || "",
          aliases: product.aliases || [],
          active: product.active !== false,
          created_at: product.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      }),
    );
    return true;
  } catch (err) {
    console.error("saveProduct error:", err);
    return false;
  }
}

/**
 * Deactivate a product (soft delete).
 */
export async function deactivateProduct(sku) {
  try {
    await db.send(
      new UpdateCommand({
        TableName: "fridgeiq-products",
        Key: { sku },
        UpdateExpression: "SET active = :a, updated_at = :ts",
        ExpressionAttributeValues: {
          ":a": false,
          ":ts": new Date().toISOString(),
        },
      }),
    );
    return true;
  } catch (err) {
    console.error("deactivateProduct error:", err);
    return false;
  }
}

/**
 * Assign a product to a machine inventory with starting quantity.
 */
export async function assignProductToMachine(
  machineId,
  product,
  startingQty = 0,
) {
  try {
    await db.send(
      new PutCommand({
        TableName: "fridgeiq-inventory",
        Item: {
          machine_id: machineId,
          sku: product.sku,
          name: product.name,
          category: product.category,
          quantity: startingQty,
          low_stock_threshold: 6,
          last_restocked: "Never",
          restocked_by: "",
        },
      }),
    );
    return true;
  } catch (err) {
    console.error("assignProductToMachine error:", err);
    return false;
  }
}

// ── Machines ──────────────────────────────────────────────────────────────────

/**
 * Get all machines from fridgeiq-machines table.
 */
export async function getMachines(machineIds) {
  try {
    const { BatchGetCommand } = await import("@aws-sdk/lib-dynamodb");
    const result = await db.send(
      new BatchGetCommand({
        RequestItems: {
          "fridgeiq-machines": {
            Keys: machineIds.map((id) => ({ machine_id: id })),
          },
        },
      }),
    );
    const items = result.Responses?.["fridgeiq-machines"] || [];
    // Return in same order as machineIds
    return machineIds.map(
      (id) =>
        items.find((m) => m.machine_id === id) || {
          machine_id: id,
          name: id,
          address: "",
        },
    );
  } catch (err) {
    console.error("getMachines error:", err);
    return machineIds.map((id) => ({ machine_id: id, name: id, address: "" }));
  }
}

/**
 * Save machine metadata (name, address, notes).
 */
export async function saveMachine(machine) {
  try {
    await db.send(
      new UpdateCommand({
        TableName: "fridgeiq-machines",
        Key: { machine_id: machine.machine_id },
        UpdateExpression:
          "SET #n = :name, address = :address, notes = :notes, updated_at = :ts",
        ExpressionAttributeNames: { "#n": "name" },
        ExpressionAttributeValues: {
          ":name": machine.name,
          ":address": machine.address || "",
          ":notes": machine.notes || "",
          ":ts": new Date().toISOString(),
        },
      }),
    );
    return true;
  } catch (err) {
    console.error("saveMachine error:", err);
    return false;
  }
}
